import { t } from "@lingui/macro";
import {
  NavigationProp,
  useNavigation,
  useRoute,
} from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { useSetAtom } from "jotai";
import { useEffect, useMemo } from "react";
import { useLazyLoadQuery } from "react-relay";

import { preAuthenticatedPath_atom } from "@/features/Authentication/atoms";
import { BaseNavigatorParamList } from "@/navigators/BaseNavigator/types";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";

import { useEventPatronTypeRedirectQuery } from "./__generated__/useEventPatronTypeRedirectQuery.graphql";

export const useEventPatronTypeRedirect = ({
  eventId,
}: {
  eventId: string;
}) => {
  const queryData = useLazyLoadQuery<useEventPatronTypeRedirectQuery>(
    useEventPatronTypeRedirectQueryString,
    {
      eventId,
    }
  );
  const { showToast } = useToast();

  const parentNavigation =
    useNavigation<NavigationProp<BaseNavigatorParamList>>();

  const accessStatusForClient = queryData?.node?.accessStatusForClient;

  const currentUserIsNotLoggedIn =
    !queryData?.session?.currentClientProfile ||
    queryData?.session?.currentClientProfile?.member?.__typename ===
      "AnonymousCustomer";

  const canViewEvent = useMemo(() => {
    return !!queryData?.node && accessStatusForClient !== "UNAVAILABLE";
  }, [accessStatusForClient, queryData?.node]);

  const setPreAuthenticatedPath = useSetAtom(preAuthenticatedPath_atom);

  const route = useRoute();
  const currentPath = route.path;
  const clubSlug = currentPath ? extractClubSlugFromPath(currentPath) : null;

  useEffect(() => {
    if (!canViewEvent) {
      if (currentUserIsNotLoggedIn) {
        if (currentPath) {
          setPreAuthenticatedPath(currentPath);
        }

        showToast({
          title: t`You must be logged in to view this event`,
          description: t`Please log in to continue`,
          semanticStyle: "error",
        });

        /**
         * If we can find the club slug -- go to the club's login.
         * If not -- ship the user to our generic login.
         */
        if (clubSlug) {
          // Navigate to the parent Club navigator's Login screen
          parentNavigation.navigate("Club", {
            clubSlug,
            screen: "Login",
            params: {
              clubSlug,
            },
          });
        } else {
          parentNavigation.navigate("Unauthenticated");
        }
      } else {
        showToast({
          title: t`Sorry, this event is no longer available`,
          semanticStyle: "error",
        });

        parentNavigation.navigate("Club", {
          clubSlug: clubSlug || "",
          screen: "Home",
          params: {
            screen: "Events",
          },
        });
      }
    }
  }, [
    canViewEvent,
    parentNavigation,
    queryData,
    showToast,
    setPreAuthenticatedPath,
    currentUserIsNotLoggedIn,
    currentPath,
    clubSlug,
  ]);

  return canViewEvent;
};

const extractClubSlugFromPath = (path: string) => {
  const match = path.match(/(?:^|\/)club\/([^/]+)(?:\/|$)/);
  return match ? decodeURIComponent(match[1]) : null;
};

export const useEventPatronTypeRedirectQueryString = graphql`
  query useEventPatronTypeRedirectQuery($eventId: ID!) {
    session {
      currentClientProfile {
        member {
          __typename
        }
      }
    }
    node(id: $eventId) {
      ... on Event {
        id
        accessStatusForClient
        eventSeries {
          facility {
            club {
              slug
            }
          }
        }
      }
    }
  }
`;
